import { useState } from 'react';
import { api } from '../api';
import { run } from '../hooks';
import type { EditorProps } from '../pages/SitePage';

/** Theme and trigger rules (planning.md FR-C-06, FR-W-04, FR-W-12). */
export function AppearanceForm({ bundle, reload, show }: EditorProps) {
  const { site } = bundle;
  const [brandName, setBrandName] = useState(site.brandName ?? '');
  const [theme, setTheme] = useState({ primary: '#0A66C2', radius: 12, font: 'inherit', mode: 'auto' as 'auto' | 'light' | 'dark', logoUrl: '', ...site.theme });
  const [trigger, setTrigger] = useState({ enabled: true, showOn: 'first_visit' as 'first_visit' | 'every_visit' | 'never', delayMs: 1200, excludePaths: [] as string[], firstVisitExpiryHours: 24, mobile: true, launcher: true, ...site.trigger });
  const [search, setSearch] = useState({ enabled: site.search?.enabled ?? false, placeholder: site.search?.placeholder ?? 'Search this site' });
  const [origins, setOrigins] = useState(site.allowedOrigins.join('\n'));
  const [excl, setExcl] = useState(trigger.excludePaths.join('\n'));

  const save = async () => {
    const t = { ...theme, logoUrl: theme.logoUrl || undefined };
    const r = await run(
      api.patchSite(site.siteId, {
        brandName,
        theme: t,
        trigger: { ...trigger, excludePaths: excl.split('\n').map((s) => s.trim()).filter(Boolean) },
        allowedOrigins: origins.split('\n').map((s) => s.trim()).filter(Boolean),
        search: { enabled: search.enabled, placeholder: search.placeholder.trim() || 'Search this site' },
      }),
      show,
      'Saved. Publish to apply.',
    );
    if (r) await reload();
  };

  return (
    <div className="grid" style={{ gap: 16 }}>
      <div className="grid cols-2">
      <div className="card grid" style={{ gap: 12 }}>
        <h2>Look</h2>
        <label className="field">
          Brand name shown in the widget header
          <input className="input" value={brandName} onChange={(e) => setBrandName(e.target.value)} />
        </label>
        <label className="field">
          Logo URL (optional)
          <input className="input" value={theme.logoUrl ?? ''} onChange={(e) => setTheme({ ...theme, logoUrl: e.target.value })} placeholder="https://…/logo.svg" />
        </label>
        <div className="row">
          <label className="field">
            Primary colour
            <div className="row">
              <input type="color" value={theme.primary} onChange={(e) => setTheme({ ...theme, primary: e.target.value.toUpperCase() })} style={{ width: 44, height: 36, border: 0, background: 'none' }} />
              <input className="input mono" style={{ width: 110 }} value={theme.primary} onChange={(e) => setTheme({ ...theme, primary: e.target.value })} />
            </div>
          </label>
          <label className="field">
            Corner radius
            <input className="input" type="number" min={0} max={32} value={theme.radius} onChange={(e) => setTheme({ ...theme, radius: Number(e.target.value) })} style={{ width: 90 }} />
          </label>
          <label className="field">
            Mode
            <select className="select" value={theme.mode} onChange={(e) => setTheme({ ...theme, mode: e.target.value as typeof theme.mode })} style={{ width: 120 }}>
              <option value="auto">auto</option>
              <option value="light">light</option>
              <option value="dark">dark</option>
            </select>
          </label>
        </div>
        <label className="field">
          Font family (CSS value, or "inherit" to match the site)
          <input className="input" value={theme.font} onChange={(e) => setTheme({ ...theme, font: e.target.value })} />
        </label>
      </div>
      <div className="card grid" style={{ gap: 12 }}>
        <h2>Behaviour</h2>
        <label className="checkbox" style={{ fontSize: 14 }}>
          <input type="checkbox" checked={trigger.enabled} onChange={(e) => setTrigger({ ...trigger, enabled: e.target.checked })} /> Springboard enabled for visitors (master switch)
        </label>
        <div className="row">
          <label className="field">
            Show on
            <select className="select" value={trigger.showOn} onChange={(e) => setTrigger({ ...trigger, showOn: e.target.value as typeof trigger.showOn })} style={{ width: 150 }}>
              <option value="first_visit">first visit</option>
              <option value="every_visit">every visit</option>
              <option value="never">never (launcher only)</option>
            </select>
          </label>
          <label className="field">
            Delay (ms)
            <input className="input" type="number" min={0} max={30000} step={100} value={trigger.delayMs} onChange={(e) => setTrigger({ ...trigger, delayMs: Number(e.target.value) })} style={{ width: 110 }} />
          </label>
          <label className="field" title="A visitor not seen for this long counts as a first visit again">
            First-visit expiry (hours)
            <input className="input" type="number" min={1} max={8760} value={trigger.firstVisitExpiryHours} onChange={(e) => setTrigger({ ...trigger, firstVisitExpiryHours: Number(e.target.value) })} style={{ width: 130 }} />
          </label>
        </div>
        <div className="row">
          <label className="checkbox">
            <input type="checkbox" checked={trigger.mobile} onChange={(e) => setTrigger({ ...trigger, mobile: e.target.checked })} /> show on mobile
          </label>
          <label className="checkbox">
            <input type="checkbox" checked={trigger.launcher} onChange={(e) => setTrigger({ ...trigger, launcher: e.target.checked })} /> launcher tab after dismissal
          </label>
        </div>
        <label className="field">
          Never open on these paths (one glob per line, e.g. /thank-you*)
          <textarea className="textarea" value={excl} onChange={(e) => setExcl(e.target.value)} />
        </label>
        <label className="field">
          Allowed origins (one per line)
          <textarea className="textarea" value={origins} onChange={(e) => setOrigins(e.target.value)} />
        </label>
        <h3 style={{ marginTop: 6 }}>Search</h3>
        <label className="checkbox" style={{ fontSize: 14 }} title="Hybrid keyword and semantic search over the scanned pages; results show as cards (planning.md FR-W-08)">
          <input type="checkbox" checked={search.enabled} onChange={(e) => setSearch({ ...search, enabled: e.target.checked })} /> Search box in the Springboard
        </label>
        {search.enabled && (
          <label className="field">
            <span>Placeholder</span>
            <input className="input" value={search.placeholder} maxLength={60} onChange={(e) => setSearch({ ...search, placeholder: e.target.value })} />
          </label>
        )}
      </div>
      </div>
      <div className="card row spread" style={{ padding: '12px 20px' }}>
        <span className="small muted">Saving keeps your changes as a draft for both Look and Behaviour. They reach visitors when you publish.</span>
        <button className="btn primary" onClick={save}>
          Save appearance
        </button>
      </div>
    </div>
  );
}
